import { useState } from "react";
import { X, Plus } from "lucide-react";

export const COMMON_AMENITIES = [
  "Garden", "Parking", "Garage", "Balcony", "Terrace", "Swimming Pool",
  "Gym", "Fireplace", "Air Conditioning", "Pets Allowed", "Furnished",
  "Off-Street Parking", "En-Suite", "Conservatory", "Double Glazing",
  "Utility Room",
];

/**
 * Multi-select chip picker for the "Features" field. Storage stays a
 * plain comma-separated string (same format the field always used) —
 * this only changes how it's entered. Presets toggle on/off; anything
 * already saved that isn't a preset shows as its own removable chip,
 * and "+ Add custom" lets you type anything not in the list.
 */
export const MAX_FEATURES = 10;

export function FeatureChipsInput({ value, onChange, presets = COMMON_AMENITIES, testIdPrefix = "feature-chip", maxItems = MAX_FEATURES }) {
  const [customInput, setCustomInput] = useState("");
  const [addingCustom, setAddingCustom] = useState(false);

  const selected = (value || "").split(",").map((s) => s.trim()).filter(Boolean);
  const customSelected = selected.filter((s) => !presets.includes(s));
  const atLimit = selected.length >= maxItems;

  const setSelected = (nextList) => onChange(nextList.slice(0, maxItems).join(", "));

  const toggle = (label) => {
    if (selected.includes(label)) {
      setSelected(selected.filter((s) => s !== label));
    } else {
      if (atLimit) return;
      setSelected([...selected, label]);
    }
  };

  const addCustom = () => {
    const v = customInput.trim();
    if (v && !selected.includes(v) && !atLimit) setSelected([...selected, v]);
    setCustomInput("");
    setAddingCustom(false);
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-input`}>
      <div className="flex flex-wrap gap-2">
        {presets.map((label) => {
          const active = selected.includes(label);
          const disabled = !active && atLimit;
          return (
            <button
              key={label}
              type="button"
              disabled={disabled}
              onClick={() => toggle(label)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                active
                  ? "bg-primary text-white border-primary"
                  : disabled
                  ? "bg-muted/50 border-border text-muted-foreground/50 cursor-not-allowed"
                  : "bg-white border-border hover:border-primary text-foreground"
              }`}
              data-testid={`${testIdPrefix}-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {label}
            </button>
          );
        })}
        {customSelected.map((label) => (
          <button
            key={label}
            type="button"
            onClick={() => toggle(label)}
            className="px-3 py-1.5 text-sm rounded-full border border-primary bg-primary/10 text-primary flex items-center gap-1.5"
            data-testid={`${testIdPrefix}-custom-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {label}
            <X className="h-3 w-3" />
          </button>
        ))}
        {addingCustom ? (
          <span className="flex items-center gap-1">
            <input
              autoFocus
              type="text"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); addCustom(); }
                if (e.key === "Escape") setAddingCustom(false);
              }}
              onBlur={addCustom}
              placeholder="Custom feature…"
              className="px-3 py-1.5 text-sm rounded-full border border-primary h-[34px] w-40"
              data-testid={`${testIdPrefix}-custom-input`}
            />
          </span>
        ) : (
          <button
            type="button"
            disabled={atLimit}
            onClick={() => setAddingCustom(true)}
            className={`px-3 py-1.5 text-sm rounded-full border border-dashed flex items-center gap-1 ${
              atLimit
                ? "border-border text-muted-foreground/50 cursor-not-allowed"
                : "border-border hover:border-primary text-muted-foreground"
            }`}
            data-testid={`${testIdPrefix}-add-custom`}
          >
            <Plus className="h-3.5 w-3.5" />
            Add custom
          </button>
        )}
      </div>
      <p
        className={`text-xs ${atLimit ? "text-amber-600" : "text-muted-foreground"}`}
        data-testid={`${testIdPrefix}-count`}
      >
        {selected.length} / {maxItems} features selected
        {atLimit ? " — remove one to add another" : ""}
      </p>
    </div>
  );
}

export default FeatureChipsInput;
